import React, { useState } from 'react';
import { Bot, Sparkles, FileText, CheckSquare, Lightbulb, BarChart3, Loader, X } from 'lucide-react';
import { summarizeMessages, extractTasks, analyzeSentiment, generateMeetingNotes, getProductivityInsights } from '../../services/chatAIService';

const AIAssistPanel = ({ channelId, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [activeFeature, setActiveFeature] = useState(null);
  const [result, setResult] = useState(null);

  const handleSummarize = async () => {
    setLoading(true);
    setActiveFeature('summarize');
    try {
      const response = await summarizeMessages(channelId);
      setResult(response.data);
    } catch (error) {
      setResult({ error: 'Failed to generate summary' });
    } finally {
      setLoading(false);
    }
  };

  const handleExtractTasks = async () => {
    setLoading(true);
    setActiveFeature('tasks');
    try {
      const response = await extractTasks(channelId);
      setResult(response.data);
    } catch (error) {
      setResult({ error: 'Failed to extract tasks' });
    } finally {
      setLoading(false);
    }
  };

  const handleInsights = async () => {
    setLoading(true);
    setActiveFeature('insights');
    try {
      const response = await getProductivityInsights(channelId);
      setResult(response.data);
    } catch (error) {
      setResult({ error: 'Failed to get insights' });
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="absolute top-16 right-4 w-80 bg-gray-800 border border-gray-700 rounded-lg shadow-2xl z-50">
      <div className="p-4 border-b border-gray-700 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Bot className="w-5 h-5 text-cyan-400" />
          <h3 className="text-white font-semibold">AI Assistant</h3>
        </div>
        <button onClick={onClose} className="text-gray-400 hover:text-white">
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="p-4 space-y-2">
        <button
          onClick={handleSummarize}
          disabled={loading}
          className="w-full flex items-center gap-3 p-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-left"
        >
          <Sparkles className="w-5 h-5 text-yellow-400" />
          <div className="flex-1">
            <div className="text-white font-medium">Summarize</div>
            <div className="text-gray-400 text-xs">Get conversation summary</div>
          </div>
        </button>

        <button
          onClick={handleExtractTasks}
          disabled={loading}
          className="w-full flex items-center gap-3 p-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-left"
        >
          <CheckSquare className="w-5 h-5 text-green-400" />
          <div className="flex-1">
            <div className="text-white font-medium">Extract Tasks</div>
            <div className="text-gray-400 text-xs">Find action items</div>
          </div>
        </button>

        <button
          onClick={handleInsights}
          disabled={loading}
          className="w-full flex items-center gap-3 p-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors text-left"
        >
          <BarChart3 className="w-5 h-5 text-blue-400" />
          <div className="flex-1">
            <div className="text-white font-medium">Insights</div>
            <div className="text-gray-400 text-xs">Team productivity</div>
          </div>
        </button>
      </div>

      {loading && (
        <div className="p-4 border-t border-gray-700 flex items-center justify-center gap-2">
          <Loader className="w-4 h-4 animate-spin text-cyan-400" />
          <span className="text-gray-400 text-sm">Processing...</span>
        </div>
      )}

      {result && !loading && (
        <div className="p-4 border-t border-gray-700 max-h-96 overflow-y-auto">
          {result.error ? (
            <div className="p-3 bg-red-900/20 border border-red-700/50 rounded-lg">
              <p className="text-red-400 text-sm">{result.error}</p>
            </div>
          ) : (
            <div className="space-y-3">
              {activeFeature === 'summarize' && (
                <div className="text-gray-300 text-sm whitespace-pre-wrap">{result.summary}</div>
              )}
              {activeFeature === 'tasks' && (
                <div className="space-y-2">
                  {result.tasks?.map((task, i) => (
                    <div key={i} className="p-2 bg-gray-700 rounded">
                      <div className="text-white text-sm">{task.description || task}</div>
                    </div>
                  ))}
                </div>
              )}
              {activeFeature === 'insights' && result.metrics && (
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Messages:</span>
                    <span className="text-white">{result.metrics.totalMessages}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-400">Avg Response:</span>
                    <span className="text-white">{result.metrics.avgResponseTime}m</span>
                  </div>
                </div>
              )}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default AIAssistPanel;
